import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { day13ToGregorian } from '@/lib/calendar13';
import type { Calendar13Config } from '@/lib/calendar13Types';
import { format } from 'date-fns';

interface DriftViewProps {
  year: number;
  config: Calendar13Config;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSyncToDrift: () => void;
}

export function DriftView({ 
  year, 
  config,
  onPrevMonth, 
  onNextMonth,
  onSyncToDrift 
}: DriftViewProps) {
  // Get the exact Gregorian date for The Drift (day 365)
  const driftDate = day13ToGregorian(year, 0, 0, config);
  const formattedDate = format(driftDate, 'MMMM d, yyyy');
  
  return (
    <div className="w-full">
      {/* Header navigation */}
      <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-2 py-2 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onPrevMonth}
          className="hover:bg-secondary/60 h-8 w-8"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        
        <div className="text-center">
          <div className="flex items-center gap-2 justify-center">
            <span className="text-2xl">✧</span>
            <h2 className="text-2xl font-display font-bold tracking-wide text-amber-400">
              The Drift
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Day Out of Time • Year {year} → {year + 1}
          </p>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onNextMonth}
          className="hover:bg-secondary/60 h-8 w-8"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Drift content - special cosmic display */}
      <div className="relative min-h-[280px] flex flex-col items-center justify-center text-center px-6 py-8 rounded-xl bg-gradient-to-b from-amber-500/10 via-transparent to-purple-500/10 border border-amber-500/20">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden rounded-xl">
          <div className="absolute top-4 left-4 w-2 h-2 bg-amber-400/60 rounded-full animate-pulse" />
          <div className="absolute top-8 right-8 w-1.5 h-1.5 bg-purple-400/60 rounded-full animate-pulse delay-300" />
          <div className="absolute bottom-6 left-12 w-1 h-1 bg-amber-300/40 rounded-full animate-pulse delay-500" />
          <div className="absolute bottom-10 right-6 w-2 h-2 bg-purple-300/40 rounded-full animate-pulse delay-700" />
        </div>

        <Sparkles className="w-12 h-12 text-amber-400/80 mb-4" />
        
        <h3 className="text-xl font-display font-semibold text-amber-200 mb-2">
          The Space Between
        </h3>
        
        {/* Exact date display */}
        <div className="mb-4 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <p className="text-xs text-amber-300/70 uppercase tracking-wider mb-1">Gregorian Date</p>
          <p className="text-lg font-display font-semibold text-amber-200">{formattedDate}</p>
        </div>
        
        <p className="text-sm text-muted-foreground max-w-xs mb-6 leading-relaxed">
          Day 365 exists outside the 13 moons—a moment of reflection between cycles. 
          Not recorded in time, yet felt just the same.
        </p>

        <Button
          onClick={onSyncToDrift}
          className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 hover:border-amber-500/60 text-amber-200 hover:text-amber-100 transition-all group"
        >
          <Sparkles className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
          Link cycles to The Drift
        </Button>

        <p className="text-[10px] text-amber-300/50 mt-4">
          +1.25 days drift accumulated yearly
        </p>
      </div>
    </div>
  );
}
